import { tokenStore } from "./tokens";

const API_HOST = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
export const API_BASE = `${API_HOST}/v1`;

export class APIError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

let refreshInFlight: Promise<boolean> | null = null;

export async function attemptRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const refresh = await tokenStore.getRefresh();
      const body = tokenStore.isNative && refresh
        ? JSON.stringify({ refresh_token: refresh })
        : "{}";
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        credentials: tokenStore.isNative ? "omit" : "include",
        headers: { "Content-Type": "application/json" },
        body,
      });
      if (!res.ok) return false;
      if (tokenStore.isNative) {
        const data = (await res.json()) as {
          access_token?: string;
          refresh_token?: string;
        };
        if (data.access_token && data.refresh_token) {
          await tokenStore.setBoth(data.access_token, data.refresh_token);
        } else if (data.access_token) {
          await tokenStore.setAccess(data.access_token);
        }
      }
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => { refreshInFlight = null; }, 0);
    }
  })();
  return refreshInFlight;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  retried = false,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init.headers as Record<string, string> | undefined) ?? {}),
  };
  if (tokenStore.isNative) {
    const access = await tokenStore.getAccess();
    if (access) headers["Authorization"] = `Bearer ${access}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    credentials: tokenStore.isNative ? "omit" : "include",
    headers,
    ...init,
  });
  if (res.status === 401 && !retried && !path.startsWith("/auth/")) {
    if (await attemptRefresh()) {
      return request<T>(path, init, true);
    }
  }
  if (!res.ok) {
    let body = "";
    try { body = await res.text(); } catch {  }
    throw new APIError(res.status, body || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") ?? "";
  if (!ct.includes("application/json")) return undefined as T;
  return (await res.json()) as T;
}

export type Device = {
  device_id: string;
  device_type: string;
  name: string;
  description: string;
  owner_id: number | null;

  owner_email?: string;
  status: string;
  created_at: string;
  updated_at: string;
};

export type UserRole = "owner" | "admin" | "support";

export type User = {
  id: number;
  email: string;
  role: UserRole;
  created_at: string;
  updated_at: string;
};

export type AuthResponse = {
  user: { id: number; email: string; role: string };
  access_token: string;
  refresh_token: string;
  expires_at: number;
};

async function captureAuth(resp: AuthResponse): Promise<AuthResponse> {
  if (tokenStore.isNative && resp.access_token && resp.refresh_token) {
    await tokenStore.setBoth(resp.access_token, resp.refresh_token);
  }
  return resp;
}

export const api = {
  login: async (email: string, password: string) => {
    const r = await request<AuthResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    return captureAuth(r);
  },

  requestMagicLink: (email: string) =>
    request<{ message: string }>("/auth/magic-link", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  verifyMagicLink: async (code: string) => {
    const r = await request<AuthResponse>("/auth/magic-link/verify", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
    return captureAuth(r);
  },

  logout: async () => {
    try {
      await request<void>("/auth/logout", { method: "POST" });
    } finally {
      await tokenStore.clear();
    }
  },

  me: () => request<{ id: number; email: string; role: string; has_password: boolean }>("/auth/me"),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ message: string }>("/users/change-password", {
      method: "POST",
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    }),

  listDevices: async (): Promise<Device[]> => {
    const r = await request<{ devices: Device[] }>("/devices");
    return r.devices ?? [];
  },
  getDevice:   (id: string) => request<Device>(`/devices/${id}`),

  updateDevice: (
    id: string,
    patch: { name?: string; description?: string },
  ) =>
    request<Device>(`/devices/${id}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),
  removeDevice: (id: string) =>
    request<void>(`/devices/${id}`, { method: "DELETE" }),
  claimDevice: (deviceID: string, name?: string) =>
    request<Device>("/devices/claim", {
      method: "POST",
      body: JSON.stringify({ device_id: deviceID, name }),
    }),

  sendCommand: (deviceID: string, payloadBase64: string) =>
    request<void>(`/devices/${deviceID}/cmd`, {
      method: "POST",
      body: JSON.stringify({ payload: payloadBase64 }),
    }),

  listUsers: async (): Promise<User[]> => {
    const r = await request<{ users: User[] }>("/admin/users");
    return r.users ?? [];
  },
  updateUserRole: (userID: number, role: UserRole) =>
    request<{ message: string }>(`/admin/users/${userID}/role`, {
      method: "PUT",
      body: JSON.stringify({ role }),
    }),
  deleteUser: (userID: number) =>
    request<{ message: string }>(`/admin/users/${userID}`, {
      method: "DELETE",
    }),
};
